import { memo, useContext } from 'react';
import { Breadcrumbs } from 'src/actuators/Breadcrumbs';
import { ConfigContext } from 'src/configContext';
import { MESSAGES } from 'src/modules/common/constants';
import { RuntimeContext } from 'src/runtimeContext';
import { BREADCRUMB_STATUS } from 'src/common/types/BreadcrumbStatus';
import { ActuatorBreadcrumbValue } from 'src/actuators/common/ActuatorBreadcrumbValue/ActuatorBreadcrumbValue';
import { ActuatorTooltipValue } from 'src/actuators/common/ActuatorTooltipValue/ActuatorTooltipValue';
import { UserInput } from 'src/actuators/user-input/types';
import { BreadcrumbContainer } from 'src/common/Breadcrumb/BreadcrumbContainer';
import { PATHS } from 'src/actuators/common/types/Paths';
import { LocationState } from 'src/actuators/LocationState';
import { RenderHtml } from 'src/common/RenderHtml/RenderHtml';
import { removeFromBreadcrumb, setFromBreadcrumb } from 'src/common/breadcrumbUtils.ts';

const ACTUATOR_BREADCRUMB_PAGES = {
    [PATHS.Home]: 0,
    [PATHS.UserInput]: 1,
    [PATHS.Actuators]: 2,
    [PATHS.Accessories]: 3,
    [PATHS.Checkout]: 4,
};

const getPageStatus = (
    comparing: keyof typeof ACTUATOR_BREADCRUMB_PAGES,
    comparedTo: keyof typeof ACTUATOR_BREADCRUMB_PAGES,
) => {
    if (ACTUATOR_BREADCRUMB_PAGES[comparing] === ACTUATOR_BREADCRUMB_PAGES[comparedTo]) {
        return BREADCRUMB_STATUS.PROGRESS;
    }

    if (ACTUATOR_BREADCRUMB_PAGES[comparing] < ACTUATOR_BREADCRUMB_PAGES[comparedTo]) {
        return BREADCRUMB_STATUS.EMPTY;
    }

    return BREADCRUMB_STATUS.DONE;
};

type Props = {
    homePageValue: string;
    userInputState: UserInput;
    resetHandler?: () => void;
    currentPage: keyof typeof ACTUATOR_BREADCRUMB_PAGES;
    isLinear: boolean;
    isHomePageInvalid?: boolean;
    isUserInputPageInvalid?: boolean;
    searchParams: URLSearchParams;
    locationState: LocationState;
}

export const ActuatorBreadcrumbs = memo(({
    homePageValue,
    userInputState,
    resetHandler,
    currentPage,
    isLinear,
    isHomePageInvalid,
    isUserInputPageInvalid,
    searchParams,
    locationState,
}: Props) => {
    const { userSettings } = useContext(ConfigContext);
    const { isMobile } = useContext(RuntimeContext);

    const isUserInputPageDisabled = ACTUATOR_BREADCRUMB_PAGES[currentPage] <
        ACTUATOR_BREADCRUMB_PAGES[PATHS.UserInput] &&
        isHomePageInvalid;
    const isActuatorsPageDisabled = isUserInputPageDisabled || isUserInputPageInvalid;
    const isAccessoriesPageDisabled = isActuatorsPageDisabled || !locationState.actuatorLabel;

    const searchParamsForBreadcrumbs = removeFromBreadcrumb(searchParams);
    const searchParamsForAccessories = setFromBreadcrumb(searchParams);

    const breadcrumbProps: Breadcrumbs = [
        {
            title: MESSAGES.SelectActuatorType,
            value: <RenderHtml data={homePageValue}/>,
            linkState: locationState,
            breadcrumbStatus: getPageStatus(currentPage, PATHS.Home),
            onActionClick: resetHandler,
            to: {
                pathname: PATHS.Home,
                search: searchParamsForBreadcrumbs.toString(),
            },
        },
        {
            title: MESSAGES.ActuatorParams,
            value: (isUserInputPageDisabled || isUserInputPageInvalid)
                ? '-'
                : (
                    <ActuatorBreadcrumbValue
                        region={userSettings.currentRegion}
                        state={userInputState}
                        currentLocale={userSettings.currentLocale}
                        isLinear={isLinear}
                    />
                ),
            tooltipValue: <ActuatorTooltipValue
                region={userSettings.currentRegion}
                state={userInputState}
                isLinear={isLinear}
                currentLocale={userSettings.currentLocale}
            />,
            linkState: locationState,
            breadcrumbStatus: getPageStatus(currentPage, PATHS.UserInput),
            disabled: isUserInputPageDisabled,
            onActionClick: resetHandler,
            to: {
                pathname: PATHS.UserInput,
                search: searchParamsForBreadcrumbs.toString(),
            },
        },
        {
            title: MESSAGES.SelectedActuator,
            value: locationState.actuatorLabel || '-',
            linkState: locationState,
            breadcrumbStatus: getPageStatus(currentPage, PATHS.Actuators),
            disabled: isActuatorsPageDisabled,
            onActionClick: resetHandler,
            to: {
                pathname: PATHS.Actuators,
                search: searchParamsForBreadcrumbs.toString(),
            },
        },
        {
            title: MESSAGES.SelectedAccessories,
            value: locationState.selectedAccessories.length > 0
                ? locationState.selectedAccessories.map(({ label }) => label).join(', ') : '-',
            linkState: locationState,
            breadcrumbStatus: getPageStatus(currentPage, PATHS.Accessories),
            disabled: isAccessoriesPageDisabled,
            onActionClick: resetHandler,
            to: {
                pathname: PATHS.Accessories,
                search: searchParamsForAccessories.toString(),
            },
        },
        {
            title: MESSAGES.checkoutActuatorsBreadcrumbTitle,
            value: '',
            linkState: locationState,
            breadcrumbStatus: getPageStatus(currentPage, PATHS.Checkout),
            disabled: isAccessoriesPageDisabled,
            onActionClick: resetHandler,
            to: {
                pathname: PATHS.Checkout,
                search: searchParamsForBreadcrumbs.toString(),
            },
        },
    ];
    return (
        <BreadcrumbContainer
            isMobile={isMobile}
            breadcrumbs={breadcrumbProps}
            containerWrapperClass="vss-actuators-breadcrumbs"
        />
    );
});
